import { StaticPageLayout } from '@/components/StaticPageLayout';
import { ArrowRight, ExternalLink, File, Star } from 'lucide-react';
import { useState } from 'react';

type Resource = {
  title: string;
  url: string;
  pdf?: { url: string }
  description?: string;
  featured?: boolean;
};

type ResourceCategory = {
  name: string;
  number: string;
  resources: Resource[];
};

type Props = {
  resource_categories?: ResourceCategory[];
};

const defaultResourceCategories: ResourceCategory[] = [
  {
    name: 'General Resources',
    number: '01',
    resources: [
      { title: 'Diversity Statement Tips', url: 'https://ibb.co/kxBHm5X' },
      {
        title: 'Empowering Female Minds in STEM Mentorship',
        url: 'https://www.empoweringfems.com/membership-form',
      },
      {
        title: 'F1 Visa Application Overview',
        url: 'https://pdfhost.io/v/f4FU9sJYR_F1_Visa_Application_Overview',
      },
      {
        title: 'General Writing Resources',
        url: 'https://drive.google.com/file/d/1GqkMDr1yvpYpdUKLBMDIavTCmKaCv5uL/view?usp=sharing',
      },
      {
        title: 'How to Mentor Minority Students',
        url: 'https://drive.google.com/file/d/1dw-8GlnJOiBxC_xCWSxo_6cFxAjmSCUU/view?usp=sharing',
      },
      {
        title: 'How to be an Effective Mentor',
        url: 'https://drive.google.com/file/d/1xSoDtzx0CMe3TFjo3MuPvQ7JywApKbGB/view?usp=sharing',
      },
      {
        title: 'Project SHORT YouTube',
        url: 'https://www.youtube.com/@projectshort2668',
      },
      {
        title: 'SHORT Talks Podcast',
        url: 'https://open.spotify.com/show/7wiefMKa4STF9rZQFHEWoP',
      },
      {
        title: 'Tips for a Great Mentor Relationship',
        url: 'https://drive.google.com/file/d/1kuuZRjosUadrsJH41b_-XVJ3x-r_5eft/view?usp=sharing',
      },
      {
        title: 'Which Language Exam? TOEFL vs IELTS',
        url: 'https://docdro.id/Tgeeon7',
      },
    ],
  },
  {
    name: 'Mental Health',
    number: '02',
    resources: [
      {
        title: 'All it takes is 10 mindful minutes',
        url: 'https://www.youtube.com/watch?v=qzR62JJCMBQ',
      },
      {
        title: 'Arts and Crafts for Mental Health',
        url: 'https://www.potomacbeads.com/the-benefit-of-arts-and-crafts-for',
      },
      { title: 'Mental Health Bootcamp', url: '#' },
      { title: 'Mindfulness during COVID', url: '#' },
    ],
  },
  {
    name: 'Medical School',
    number: '03',
    resources: [
      { title: 'Anki Flash Cards', url: '#' },
      { title: 'Medical School Application Guide', url: '#' },
      { title: 'Post-Acceptance Medical School Guide', url: '#' },
    ],
  },
  {
    name: 'Graduate School',
    number: '04',
    resources: [
      { title: 'Graduate School Application Primer', url: '#' },
      { title: 'Advice for First-Generation Students', url: '#' },
      { title: 'Recommendation Letter Request Guide', url: '#' },
      { title: 'Program Selection Resources', url: '#' },
      { title: 'Informational Interview Guide', url: '#' },
      { title: 'Application Checklists', url: '#' },
      { title: 'Graduate Programs without GRE', url: '#' },
      { title: 'Marginalized Student Survival Kit', url: '#' },
      { title: "Master's vs PhD Comparison", url: '#' },
      { title: 'Grad School Planning Checklist', url: '#' },
    ],
  },
  {
    name: 'CV & Resume',
    number: '05',
    resources: [
      { title: 'CV Advice and Guidelines', url: '#' },
      { title: 'CV vs Resume Comparison', url: '#' },
      { title: 'CV Templates', url: '#' },
      { title: 'Online Resume Builder', url: '#' },
    ],
  },
  {
    name: 'Personal Statements',
    number: '06',
    resources: [
      { title: 'Diversity Statement Resources', url: '#' },
      { title: 'Example Personal Statements', url: '#' },
    ],
  },
];

export default function Resources({ resource_categories }: Props) {
  const resourceCategories = resource_categories || defaultResourceCategories;
  const [selectedCategory, setSelectedCategory] = useState<ResourceCategory>(
    resourceCategories[0]
  );
  return (
    <StaticPageLayout>    <div className="bg-white overflow-hidden">
      {/* Hero Section */}
      <section className="bg-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl py-12 md:py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-primary mb-4">
                COMPREHENSIVE RESOURCES
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6">
                Your
                <span className="italic"> pathway to</span>
                <br />
                <span className="font-semibold">success</span>
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed max-w-2xl">
                Project SHORT provides resources to be a prepared mentor and
                applicant, compiled from our community of 1000+ mentors across
                diverse STEM fields.
              </p>
            </div>

            {/* Creative Visual - DNA Helix of Knowledge */}
            <div className="relative h-[500px] hidden lg:block">
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 400 500"
                className="absolute inset-0"
              >
                {/* DNA double helix structure */}
                {[...Array(20)].map((_, i) => {
                  const y = 50 + i * 20;
                  const offset = Math.sin(i * 0.5) * 50;
                  const opacity = 0.1 + (i % 3) * 0.05;

                  return (
                    <g key={i}>
                      {/* Left strand */}
                      <circle
                        cx={200 - offset}
                        cy={y}
                        r="4"
                        fill="rgb(147 51 234)"
                        opacity={opacity}
                        className="animate-pulse"
                        style={{ animationDelay: `${i * 0.1}s` }}
                      />
                      {/* Right strand */}
                      <circle
                        cx={200 + offset}
                        cy={y}
                        r="4"
                        fill="rgb(147 51 234)"
                        opacity={opacity}
                        className="animate-pulse"
                        style={{ animationDelay: `${i * 0.1 + 0.5}s` }}
                      />
                      {/* Connecting lines */}
                      {i % 2 === 0 && (
                        <line
                          x1={200 - offset}
                          y1={y}
                          x2={200 + offset}
                          y2={y}
                          stroke="rgb(147 51 234)"
                          strokeWidth="1"
                          opacity={opacity * 0.5}
                          className="animate-fade"
                          style={{ animationDelay: `${i * 0.2}s` }}
                        />
                      )}
                    </g>
                  );
                })}

                {/* Data flowing through the helix */}
                {[...Array(5)].map((_, i) => (
                  <circle
                    key={`flow-${i}`}
                    r="2"
                    fill="rgb(147 51 234)"
                    opacity="0.4"
                  >
                    <animateMotion
                      dur={`${8 + i * 2}s`}
                      repeatCount="indefinite"
                      begin={`${i * 1.5}s`}
                    >
                      <mpath href="#helix-path" />
                    </animateMotion>
                  </circle>
                ))}

                {/* Hidden path for animation */}
                <defs>
                  <path
                    id="helix-path"
                    d="M 200 50 Q 150 100 200 150 T 200 250 Q 250 300 200 350 T 200 450"
                    fill="none"
                  />
                </defs>

                {/* Floating particles representing knowledge bits */}
                {[...Array(30)].map((_, i) => (
                  <circle
                    key={`particle-${i}`}
                    cx={100 + Math.random() * 200}
                    cy={50 + Math.random() * 400}
                    r="1"
                    fill="rgb(147 51 234)"
                    opacity={0.2 + Math.random() * 0.3}
                    className="animate-twinkle"
                    style={{
                      animationDelay: `${Math.random() * 5}s`,
                      animationDuration: `${3 + Math.random() * 4}s`,
                    }}
                  />
                ))}

                {/* Bottom text */}
                <text
                  x="200"
                  y="480"
                  textAnchor="middle"
                  className="text-sm fill-gray-400 font-light"
                >
                  Knowledge intertwined, paths connected
                </text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-6 pb-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Category Navigation */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500 mb-8">
                  Browse Categories
                </h2>
                <nav className="space-y-2">
                  {resourceCategories.map((category, _) => {
                    const isActive =
                      selectedCategory.number === category.number;
                    return (
                      <button
                        key={category.number}
                        onClick={() => setSelectedCategory(category)}
                        className={`w-full text-left group transition-all duration-300 relative ${isActive ? 'bg-primary/5' : 'hover:bg-gray-50'
                          } rounded-lg`}
                      >
                        <div className="flex items-center justify-between px-4 py-4">
                          <div className="flex items-center gap-4">
                            {/* Category Icon/Number */}
                            <div
                              className={`
                              w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                              transition-all duration-300
                              ${isActive
                                  ? 'bg-primary text-white'
                                  : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                                }
                            `}
                            >
                              {category.number}
                            </div>

                            {/* Category Name */}
                            <div className="flex-1">
                              <h3
                                className={`font-medium transition-colors ${isActive ? 'text-gray-900' : 'text-gray-700'
                                  }`}
                              >
                                {category.name}
                              </h3>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {category.resources.length} resources
                              </p>
                            </div>
                          </div>

                          {/* Arrow indicator */}
                          <ArrowRight
                            className={`w-4 h-4 transition-all ${isActive
                              ? 'text-primary translate-x-0 opacity-100'
                              : 'text-gray-400 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-60'
                              }`}
                          />
                        </div>

                        {/* Active indicator bar */}
                        {isActive && (
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-lg" />
                        )}
                      </button>
                    );
                  })}
                </nav>

                {/* Additional Info */}
                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">
                      {resourceCategories.reduce(
                        (acc, cat) => acc + cat.resources.length,
                        0
                      )}
                    </span>{' '}
                    total resources available across all categories
                  </p>
                </div>
              </div>
            </div>

            {/* Resource List */}
            <div className="lg:col-span-2">
              <div className="mb-12">
                <div className="flex items-end justify-between mb-8">
                  <div>
                    <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-2">
                      {selectedCategory.name}
                    </h2>
                    <p className="text-sm text-gray-500">
                      Curated resources to support your journey
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {selectedCategory.resources.length} items
                    </span>
                  </div>
                </div>
                <div className="h-0.5 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent"></div>
              </div>

              <div className="space-y-4">
                {selectedCategory.resources.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.url ? resource.url : resource.pdf ? resource.pdf.url : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="p-6 bg-white border border-gray-200 rounded-lg transition-all duration-300 hover:border-primary/30 hover:shadow-md relative overflow-hidden">
                      {/* Hover effect background */}
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <div className="relative">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            {resource.featured && (
                              <div className="flex-shrink-0 mt-0.5">
                                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary transition-colors mb-1">
                                {resource.title}
                              </h3>
                              {resource.description && (
                                <p className="text-sm text-gray-600 line-clamp-2">
                                  {resource.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-primary/10 flex items-center justify-center transition-all duration-300">
                              {resource.url ? <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
                                : resource.pdf?.url && <File className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
    </StaticPageLayout>

  );
}
